import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import axios from "axios";
import "@/App.css";
import PinBoard from "@/components/pinboard/PinBoard";
import Tabs from "@/components/pinboard/Tabs";
import NoteDialog from "@/components/pinboard/NoteDialog";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Plus, Barcode, List as MenuIcon, SignOut, UserSwitch, UserPlus, TrashSimple, Article, Database, FloppyDisk, Desktop, Camera, Smiley, Briefcase, CurrencyEur, Key, Table, SquaresFour, DotsThreeVertical } from "@phosphor-icons/react";
import { BRAND, NOTE_TYPES, CHANNELS, PEOPLE } from "@/components/pinboard/constants";
import { printNote, printLabel } from "@/components/pinboard/printNote";
import ScanDialog from "@/components/pinboard/ScanDialog";
import PartialDialog from "@/components/pinboard/PartialDialog";
import LoginScreen from "@/auth/LoginScreen";
import PinScreen from "@/auth/PinScreen";
import UsersDialog from "@/auth/UsersDialog";
import DeletedNotesDialog from "@/auth/DeletedNotesDialog";
import LogsDialog from "@/auth/LogsDialog";
import DbSizeDialog from "@/auth/DbSizeDialog";
import BackupDialog from "@/auth/BackupDialog";
import NotesTable from "@/components/pinboard/NotesTable";
import ContractsView from "@/contracts/ContractsView";
import BalancesView from "@/balances/BalancesView";
import CodesView from "@/codes/CodesView";
import CameraScan from "@/components/pinboard/CameraScan";
import { ArrowsClockwise, Trash, Printer, Calendar as CalendarIcon, Pencil, CheckCircle } from "@phosphor-icons/react";
import { useAuth } from "@/auth/AuthContext";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import {
  Dialog as ScanInputDialog,
  DialogContent as ScanInputContent,
  DialogHeader as ScanInputHeader,
  DialogTitle as ScanInputTitle,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input as ScanTextInput } from "@/components/ui/input";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SNAPSHOT_KEY = "pinboard:allMinSnapshot";

function App() {
  const { token, user, activeEmployee, checking } = useAuth();

  // Loading state while we verify the token at boot
  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center board-bg">
        <Toaster position="bottom-right" richColors />
        <p className="text-[10px] uppercase tracking-[0.25em] text-ink/50 font-bold">
          ΦΟΡΤΩΣΗ...
        </p>
      </div>
    );
  }

  if (!token || !user) {
    return (
      <>
        <Toaster position="bottom-right" richColors />
        <LoginScreen />
      </>
    );
  }

  if (!activeEmployee) {
    return (
      <>
        <Toaster position="bottom-right" richColors />
        <PinScreen />
      </>
    );
  }

  return <Board />;
}

function Board() {
  const { user, activeEmployee, logout, switchEmployee, viewMode, setViewMode, theme, setTheme } = useAuth();
  const isMobile = viewMode === "mobile";
  const isSerious = theme === "serious";
  const isSupervisor = user?.role === "supervisor";
  const [usersOpen, setUsersOpen] = useState(false);
  const [deletedOpen, setDeletedOpen] = useState(false);
  const [logsOpen, setLogsOpen] = useState(false);
  const [dbSizeOpen, setDbSizeOpen] = useState(false);
  const [backupOpen, setBackupOpen] = useState(false);
  const [showContracts, setShowContracts] = useState(false);
  const [showBalances, setShowBalances] = useState(false);
  const [showCodes, setShowCodes] = useState(false);
  const [codesPinOpen, setCodesPinOpen] = useState(false);
  const [codesPin, setCodesPin] = useState("");
  const [codesPinError, setCodesPinError] = useState("");
  const [pendingContractId, setPendingContractId] = useState(null);
  const openContractById = useCallback((id) => {
    setPendingContractId(id);
    setShowContracts(true);
  }, []);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTabs, setActiveTabs] = useState(() => new Set(["all"]));
  const [activeView, setActiveView] = useState("board"); // "board" | "table"

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("create");
  const [editingNote, setEditingNote] = useState(null);
  const [defaultType, setDefaultType] = useState("task");

  // Mass minimize / restore snapshot
  const [allMinSnapshot, setAllMinSnapshot] = useState(() => {
    try {
      const raw = localStorage.getItem(SNAPSHOT_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const isAllMinimized = allMinSnapshot !== null;

  // Scan dialog state
  const [scanOpen, setScanOpen] = useState(false);
  const [scanNote, setScanNote] = useState(null);
  const [scanMode, setScanMode] = useState("scan"); // "scan" | "complete"
  const [scanInputOpen, setScanInputOpen] = useState(false);
  const [scanInputValue, setScanInputValue] = useState("");
  const [cameraScanOpen, setCameraScanOpen] = useState(false);

  // Partial execution dialog state
  const [partialOpen, setPartialOpen] = useState(false);
  const [partialNote, setPartialNote] = useState(null);

  // Per-user layout key — each employee gets their own column/order overrides
  const userKey = activeEmployee?.id || "";

  // Fetch notes
  const fetchNotes = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/notes`, {
        params: userKey ? { user_key: userKey } : {},
      });
      setNotes(data.notes || []);
    } catch (e) {
      console.error("Failed to fetch notes", e);
      toast.error("ΑΠΟΤΥΧΙΑ ΦΟΡΤΩΣΗΣ");
    } finally {
      setLoading(false);
    }
  }, [userKey]);

  // Silent refresh used by the 30s auto-poll — never shows error toasts to
  // avoid spamming the user if the connection blips.
  const silentRefresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/notes`, {
        params: userKey ? { user_key: userKey } : {},
      });
      setNotes(data.notes || []);
    } catch (e) {
      // swallow — next tick will retry
    }
  }, [userKey]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  // Auto-refresh every 30s so new posts from other users (other PCs) appear
  // automatically. We pause polling when ANY dialog is open (to preserve
  // in-progress user input) or when the browser tab is hidden (saves
  // server/battery resources). Also pauses while the contracts view is open.
  const dialogOpenRef = useRef(false);
  dialogOpenRef.current =
    dialogOpen ||
    partialOpen ||
    scanOpen ||
    scanInputOpen ||
    usersOpen ||
    deletedOpen ||
    logsOpen ||
    dbSizeOpen ||
    backupOpen ||
    showContracts;

  useEffect(() => {
    const tick = () => {
      if (dialogOpenRef.current) return;
      if (typeof document !== "undefined" && document.hidden) return;
      silentRefresh();
    };
    const handle = setInterval(tick, 30000);
    // Also refresh immediately when the tab regains focus after being hidden
    const onVisible = () => {
      if (document.visibilityState === "visible" && !dialogOpenRef.current) {
        silentRefresh();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(handle);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [silentRefresh]);

  // ── Scan handlers ────────────────────────────────────────────────────────

  const openScanById = useCallback(
    (idOrSeq) => {
      const v = String(idOrSeq || "").trim();
      if (!v) return;
      // Match by full UUID, by 6-digit seq, or by numeric value (strip leading zeros)
      const numeric = v.replace(/^0+/, "") || "0";
      const note = notes.find(
        (n) =>
          n.id === v ||
          n.seq === v ||
          (n.seq && n.seq.replace(/^0+/, "") === numeric)
      );
      if (!note) {
        toast.error("ΔΕΝ ΒΡΕΘΗΚΕ Η ΠΑΡΑΓΓΕΛΙΑ");
        return;
      }
      setScanNote(note);
      setScanMode("scan");
      setScanOpen(true);
    },
    [notes]
  );

  // Check URL ?scan=<id> on initial load (after notes have arrived)
  useEffect(() => {
    if (loading) return;
    const params = new URLSearchParams(window.location.search);
    const scanId = params.get("scan");
    if (scanId) {
      openScanById(scanId);
      // Clear from URL
      const url = new URL(window.location.href);
      url.searchParams.delete("scan");
      window.history.replaceState({}, "", url.toString());
    }
  }, [loading, openScanById]);

  const handleScanAssign = async (note, person) => {
    await handleUpdate(
      note.id,
      { in_progress: true, in_progress_by: person, read: true },
      { toast: `ΣΕ ΕΞΕΛΙΞΗ ΑΠΟ ${person}` }
    );
  };

  const promptAutoRenew = useCallback(async (note) => {
    const cid = note?.data?.contract_id;
    if (!cid) return;
    // Calculate the new expiry preview
    let preview = "";
    const old = note?.data?.contract_expiry || "";
    if (old) {
      const [y, m, d] = old.split("T")[0].split("-");
      if (y && m && d) {
        preview = ` (ΛΗΞΗ ΑΠΟ ${d}/${m}/${y} → ${d}/${m}/${parseInt(y, 10) + 1})`;
      }
    }
    if (
      !window.confirm(
        `ΑΥΤΟΜΑΤΗ ΑΝΑΝΕΩΣΗ ΤΟΥ ΣΥΜΒΟΛΑΙΟΥ ΓΙΑ 1 ΕΤΟΣ;${preview}`
      )
    )
      return;
    try {
      const resp = await axios.post(`${API}/contracts/${cid}/auto-renew`);
      const newExp = resp?.data?.expiry_date || "";
      toast.success(
        newExp
          ? `ΣΥΜΒΟΛΑΙΟ ΑΝΑΝΕΩΘΗΚΕ ΕΩΣ ${newExp.split("-").reverse().join("/")}`
          : "ΣΥΜΒΟΛΑΙΟ ΑΝΑΝΕΩΘΗΚΕ"
      );
    } catch (e) {
      toast.error("ΣΦΑΛΜΑ ΑΝΑΝΕΩΣΗΣ ΣΥΜΒΟΛΑΙΟΥ");
    }
  }, []);

  // If an order note has a mix of delivered and undelivered items, ask the
  // operator whether to split it: keep only the delivered portion on the
  // current note and create a new sibling for the remainder. Returns true if
  // the user confirmed (caller must continue with the original action).
  const maybeSplitPartialOrder = async (note) => {
    if (note?.type !== "order") return;
    const items = Array.isArray(note?.data?.items) ? note.data.items : [];
    if (!items.length) return;
    let hasDelivered = false;
    let hasRemaining = false;
    for (const it of items) {
      const qty = Math.max(1, parseInt(it.qty ?? 1, 10) || 1);
      const delivered = Math.max(
        0,
        Math.min(qty, parseInt(it.delivered ?? (it.done ? qty : 0), 10) || 0)
      );
      if (delivered > 0) hasDelivered = true;
      if (delivered < qty) hasRemaining = true;
      if (hasDelivered && hasRemaining) break;
    }
    if (!(hasDelivered && hasRemaining)) return;
    if (
      !window.confirm(
        "ΜΕΡΙΚΩΣ ΕΚΤΕΛΕΣΜΕΝΗ ΠΑΡΑΓΓΕΛΙΑ.\n\nΝΑ ΧΡΕΩΘΟΥΝ ΜΟΝΟ ΤΑ ΠΑΡΑΔΟΜΕΝΑ ΚΑΙ ΝΑ ΔΗΜΙΟΥΡΓΗΘΕΙ ΝΕΑ ΠΑΡΑΓΓΕΛΙΑ ΜΕ ΤΑ ΥΠΟΛΟΙΠΑ;"
      )
    )
      return;
    try {
      const { data } = await axios.post(
        `${API}/notes/${note.id}/split-partial`
      );
      if (data?.split) {
        toast.success(`ΔΗΜΙΟΥΡΓΗΘΗΚΕ POST #${data.new_seq} ΜΕ ΤΑ ΥΠΟΛΟΙΠΑ`);
        await fetchNotes();
      }
    } catch (e) {
      toast.error("ΣΦΑΛΜΑ ΔΙΑΧΩΡΙΣΜΟΥ");
    }
  };

  const handleScanComplete = async (note, needsPayment) => {
    if (needsPayment) {
      await maybeSplitPartialOrder(note);
      // Re-fetch may have changed the note shape; re-read latest from state
      // is unnecessary because the update below targets by id and the server
      // already trimmed items.
      // Mark as needs_payment but stay active (NOT completed). Will be completed on ΧΡΕΩΘΗΚΕ.
      await handleUpdate(
        note.id,
        {
          in_progress: false,
          executed_by: note.in_progress_by || note.assigned_to || null,
          needs_payment: true,
        },
        { toast: "ΕΚΚΡΕΜΕΙ ΧΡΕΩΣΗ" }
      );
    } else {
      await maybeSplitPartialOrder(note);
      await handleUpdate(
        note.id,
        {
          completed: true,
          in_progress: false,
          executed_by: note.in_progress_by || note.assigned_to || null,
          needs_payment: false,
        },
        { toast: "ΕΚΤΕΛΕΣΤΗΚΕ" }
      );
      // After completion: if this post-it came from a contract, offer auto-renew
      promptAutoRenew(note);
    }
  };

  const handleCharged = async (note) => {
    await handleUpdate(
      note.id,
      { completed: true, needs_payment: false },
      { toast: "ΧΡΕΩΘΗΚΕ · ΕΚΤΕΛΕΣΤΗΚΕ" }
    );
    // After paid completion: if this post-it came from a contract, offer auto-renew
    promptAutoRenew(note);
  };

  const handleScanCorrect = (note) => {
    openEdit(note);
  };

  const submitScanInput = () => {
    const v = scanInputValue.trim();
    if (!v) return;
    setScanInputOpen(false);
    setScanInputValue("");
    openScanById(v);
  };

  // ── CRUD ─────────────────────────────────────────────────────────────────

  const handleCreate = async (payload) => {
    try {
      // Place new note at row 0, column 4 (5th column). Bump existing
      // active notes in that column down by 1.
      const TARGET_COL = 4;
      const targetColNotes = notes
        .filter((n) => !n.minimized && !n.completed && (n.position?.column ?? 0) === TARGET_COL)
        .sort((a, b) => (a.position?.order ?? 0) - (b.position?.order ?? 0));
      const reorderItems = targetColNotes.map((n, idx) => ({
        id: n.id,
        position: { column: TARGET_COL, order: idx + 1 },
      }));
      if (reorderItems.length) {
        await axios.post(`${API}/notes/positions`, { items: reorderItems }, {
          params: userKey ? { user_key: userKey } : {},
        });
      }
      const { data } = await axios.post(`${API}/notes`, {
        type: payload.type,
        data: payload.data,
        channels: payload.channels || [],
        note_date: payload.note_date || null,
        assigned_to: payload.assigned_to || null,
        position: { column: TARGET_COL, order: 0 },
      });
      // If this user has personal layouts, also overlay the new note's
      // position so it shows at col 4 row 0 for them.
      if (userKey) {
        try {
          await axios.post(
            `${API}/notes/positions`,
            { items: [{ id: data.id, position: { column: TARGET_COL, order: 0 } }] },
            { params: { user_key: userKey } }
          );
        } catch (e) {
          console.error("Failed to overlay new note position", e);
        }
      }
      // local update
      setNotes((prev) => {
        const bumped = prev.map((n) => {
          const reorder = reorderItems.find((r) => r.id === n.id);
          return reorder ? { ...n, position: reorder.position } : n;
        });
        return [...bumped, data];
      });
      toast.success("ΚΑΤΑΧΩΡΗΘΗΚΕ");
      setDialogOpen(false);
    } catch (e) {
      console.error(e);
      toast.error("ΑΠΟΤΥΧΙΑ ΚΑΤΑΧΩΡΗΣΗΣ");
    }
  };

  const handleUpdate = async (id, patch, opts = {}) => {
    try {
      const { data } = await axios.put(`${API}/notes/${id}`, patch);
      setNotes((prev) =>
        prev.map((n) => {
          if (n.id !== id) return n;
          // Preserve the local (possibly per-user-overlayed) position unless
          // the patch explicitly changed it. The backend always returns the
          // global position, which would otherwise snap drag-positioned
          // notes back to their original spot.
          const preservedPosition = patch.position ? data.position : n.position;
          const preservedMinimized =
            patch.minimized !== undefined ? data.minimized : n.minimized;
          return {
            ...data,
            position: preservedPosition,
            minimized: preservedMinimized,
            _blink_at: Date.now(),
          };
        })
      );
      if (opts.toast) toast.success(opts.toast);
      return data;
    } catch (e) {
      console.error(e);
      toast.error("ΑΠΟΤΥΧΙΑ ΕΝΗΜΕΡΩΣΗΣ");
    }
  };

  const handleSaveEdit = async (payload) => {
    if (!editingNote) return;
    await handleUpdate(
      editingNote.id,
      {
        type: payload.type,
        data: payload.data,
        channels: payload.channels,
        note_date: payload.note_date || null,
        assigned_to: payload.assigned_to || null,
      },
      { toast: "ΕΝΗΜΕΡΩΘΗΚΕ" }
    );
    setDialogOpen(false);
    setEditingNote(null);
  };

  const handleDelete = async (noteOrId) => {
    // Accept either a note object or a raw id (some call sites pass the whole note)
    const id = typeof noteOrId === "string" ? noteOrId : noteOrId?.id;
    if (!id) {
      toast.error("ΑΠΟΤΥΧΙΑ ΔΙΑΓΡΑΦΗΣ");
      return;
    }
    try {
      await axios.delete(`${API}/notes/${id}`);
      setNotes((prev) => prev.filter((n) => n.id !== id));
      toast.success("ΔΙΑΓΡΑΦΗΚΕ");
    } catch (e) {
      console.error(e);
      toast.error("ΑΠΟΤΥΧΙΑ ΔΙΑΓΡΑΦΗΣ");
    }
  };

  // Drop on column/order — reorder list & persist
  const handleReorder = async (noteId, targetCol, targetOrder) => {
    // Build new state: remove the note from any column, then insert at target
    const note = notes.find((n) => n.id === noteId);
    if (!note) return;
    const sameCol = note.position.column === targetCol;

    // Compute new positions for ALL active (non-completed, non-minimized) notes
    // Group by column, sorted by order, then remove and reinsert
    const active = notes.filter((n) => !n.completed && !n.minimized);
    const byCol = {};
    active.forEach((n) => {
      const c = n.position.column;
      if (!byCol[c]) byCol[c] = [];
      byCol[c].push(n);
    });
    Object.values(byCol).forEach((arr) =>
      arr.sort((a, b) => a.position.order - b.position.order)
    );

    // Remove from old column
    const oldCol = note.position.column;
    if (byCol[oldCol]) {
      byCol[oldCol] = byCol[oldCol].filter((n) => n.id !== noteId);
    }
    // Insert into target column at target order
    if (!byCol[targetCol]) byCol[targetCol] = [];
    const insertAt = Math.max(0, Math.min(targetOrder, byCol[targetCol].length));
    byCol[targetCol].splice(insertAt, 0, note);

    // Re-flatten and create updates
    const items = [];
    Object.entries(byCol).forEach(([col, arr]) => {
      arr.forEach((n, idx) => {
        const newPos = { column: parseInt(col, 10), order: idx };
        if (
          n.position.column !== newPos.column ||
          n.position.order !== newPos.order
        ) {
          items.push({ id: n.id, position: newPos });
        }
      });
    });

    // Optimistic state update
    setNotes((prev) =>
      prev.map((n) => {
        const upd = items.find((i) => i.id === n.id);
        return upd ? { ...n, position: upd.position } : n;
      })
    );

    if (items.length) {
      try {
        await axios.post(`${API}/notes/positions`, { items }, {
          params: userKey ? { user_key: userKey } : {},
        });
      } catch (e) {
        console.error("Failed to persist positions", e);
      }
    }
  };

  const handleToggleChannel = (note, channel) => {
    const next = note.channels.includes(channel)
      ? note.channels.filter((c) => c !== channel)
      : [...note.channels, channel];
    handleUpdate(note.id, { channels: next });
  };

  const handleToggleRead = (note) => {
    handleUpdate(note.id, { read: !note.read });
  };

  const handleSetInProgress = (note, by) => {
    if (by === null) {
      handleUpdate(note.id, { in_progress: false, in_progress_by: "" });
    } else {
      handleUpdate(note.id, { in_progress: true, in_progress_by: by });
    }
  };

  const handleComplete = (note) => {
    if (!note.in_progress_by) {
      toast.error("ΑΠΑΙΤΕΙΤΑΙ ΑΝΑΘΕΣΗ ΣΕ ΥΠΑΛΛΗΛΟ ΠΡΩΤΑ");
      return;
    }
    // Open the scan-complete workflow (€ ή ΟΧΙ)
    setScanNote(note);
    setScanMode("complete");
    setScanOpen(true);
  };

  const handleUncomplete = (note) => {
    handleUpdate(note.id, { completed: false, needs_payment: false }, { toast: "ΕΠΑΝΑΦΕΡΘΗΚΕ" });
  };

  const handleChangeCategory = (note, newType) => {
    handleUpdate(note.id, { type: newType }, { toast: "ΜΕΤΑΦΕΡΘΗΚΕ" });
  };

  const handleMinimize = async (note) => {
    const willMinimize = !note.minimized;
    if (willMinimize) {
      // Just minimize, no repositioning needed.
      handleUpdate(note.id, { minimized: true });
      return;
    }
    // Restoring: move the note to row 0, column 4 (5th column), bumping
    // existing active notes in that column down by 1.
    const TARGET_COL = 4;
    const targetColNotes = notes
      .filter(
        (n) =>
          n.id !== note.id &&
          !n.minimized &&
          !n.completed &&
          (n.position?.column ?? 0) === TARGET_COL
      )
      .sort((a, b) => (a.position?.order ?? 0) - (b.position?.order ?? 0));
    const reorderItems = targetColNotes.map((n, idx) => ({
      id: n.id,
      position: { column: TARGET_COL, order: idx + 1 },
    }));
    setNotes((prev) =>
      prev.map((n) => {
        if (n.id === note.id) {
          return {
            ...n,
            minimized: false,
            position: { column: TARGET_COL, order: 0 },
          };
        }
        const reorder = reorderItems.find((r) => r.id === n.id);
        return reorder ? { ...n, position: reorder.position } : n;
      })
    );
    try {
      if (reorderItems.length) {
        await axios.post(
          `${API}/notes/positions`,
          { items: reorderItems },
          { params: userKey ? { user_key: userKey } : {} }
        );
      }
      await axios.put(`${API}/notes/${note.id}`, {
        minimized: false,
        position: { column: TARGET_COL, order: 0 },
      });
      if (userKey) {
        await axios.post(
          `${API}/notes/positions`,
          { items: [{ id: note.id, position: { column: TARGET_COL, order: 0 } }] },
          { params: { user_key: userKey } }
        );
      }
    } catch (e) {
      console.error("Restore failed", e);
      toast.error("ΑΠΟΤΥΧΙΑ ΕΠΑΝΑΦΟΡΑΣ");
    }
  };

  const openPartial = (note) => {
    setPartialNote(note);
    setPartialOpen(true);
  };

  const handlePartialSave = async (note, newItems) => {
    await handleUpdate(
      note.id,
      { data: { ...note.data, items: newItems } },
      { toast: "ΕΝΗΜΕΡΩΘΗΚΑΝ ΕΙΔΗ" }
    );
  };

  const handlePrint = (note) => {
    printNote(note);
  };

  const handlePrintLabel = (note) => {
    printLabel(note);
  };

  const handleToggleMinimizeAll = async () => {
    const candidates = notes.filter((n) => !n.completed);
    if (!isAllMinimized) {
      // Snapshot current state and minimize all non-completed
      const snapshot = {};
      candidates.forEach((n) => {
        snapshot[n.id] = !!n.minimized;
      });
      localStorage.setItem(SNAPSHOT_KEY, JSON.stringify(snapshot));
      setAllMinSnapshot(snapshot);
      const toMin = candidates.filter((n) => !n.minimized);
      setNotes((prev) =>
        prev.map((n) => (!n.completed ? { ...n, minimized: true } : n))
      );
      try {
        await Promise.all(
          toMin.map((n) =>
            axios.put(`${API}/notes/${n.id}`, { minimized: true })
          )
        );
        toast.success("ΕΛΑΧΙΣΤΟΠΟΙΗΘΗΚΑΝ ΟΛΑ");
      } catch (e) {
        console.error(e);
        toast.error("ΑΠΟΤΥΧΙΑ");
      }
    } else {
      // Restore each note to its snapshot value
      const updates = [];
      candidates.forEach((n) => {
        const prevMin = allMinSnapshot[n.id];
        if (prevMin !== undefined && n.minimized !== prevMin) {
          updates.push(
            axios.put(`${API}/notes/${n.id}`, { minimized: prevMin })
          );
        }
      });
      setNotes((prev) =>
        prev.map((n) => {
          const prevMin = allMinSnapshot[n.id];
          return prevMin !== undefined ? { ...n, minimized: prevMin } : n;
        })
      );
      try {
        await Promise.all(updates);
        toast.success("ΕΠΑΝΑΦΟΡΑ");
      } catch (e) {
        console.error(e);
      }
      localStorage.removeItem(SNAPSHOT_KEY);
      setAllMinSnapshot(null);
    }
  };

  const openCreate = (type = "task") => {
    setDialogMode("create");
    setDefaultType(type);
    setEditingNote(null);
    setDialogOpen(true);
  };

  const openEdit = (note) => {
    setDialogMode("edit");
    setEditingNote(note);
    setDialogOpen(true);
  };

  // ── Filtering by tab ─────────────────────────────────────────────────────

  const filteredNotes = useMemo(() => {
    // "completed" tab → exclusive: show only completed notes (table view)
    if (activeTabs.has("completed")) return notes.filter((n) => n.completed);

    const wantsAll = activeTabs.has("all");
    const wantsNeedsPayment = activeTabs.has("needs_payment");
    const selectedTypes = new Set(
      [...activeTabs].filter((v) => v !== "all" && v !== "needs_payment")
    );

    return notes.filter((n) => {
      if (n.completed) return false;
      // Match by selected type
      if (selectedTypes.has(n.type)) return true;
      // Match by needs_payment selection
      if (n.needs_payment && wantsNeedsPayment) return true;
      // Match by "all" — but ΟΛΑ never includes ΠΡΟΣ ΧΡΕΩΣΗ unless explicitly added
      if (wantsAll && !n.needs_payment) return true;
      if (wantsAll && wantsNeedsPayment && n.needs_payment) return true;
      return false;
    });
  }, [notes, activeTabs]);

  // Group into columns for layout
  const visibleColumns = useMemo(() => {
    const cols = {};
    filteredNotes
      .filter((n) => !n.minimized)
      .forEach((n) => {
        const c = n.position?.column ?? 0;
        if (!cols[c]) cols[c] = [];
        cols[c].push(n);
      });
    Object.values(cols).forEach((arr) =>
      arr.sort((a, b) => (a.position?.order ?? 0) - (b.position?.order ?? 0))
    );
    // Convert to array index based; ensure at least 1 empty column for drop targets
    const maxCol = Math.max(0, ...Object.keys(cols).map((k) => parseInt(k, 10)));
    const result = [];
    for (let i = 0; i <= maxCol + 1; i++) {
      result.push(cols[i] || []);
    }
    return result;
  }, [filteredNotes]);

  const minimizedNotes = useMemo(
    () => filteredNotes.filter((n) => n.minimized),
    [filteredNotes]
  );

  const activeCount = notes.filter((n) => !n.completed).length;
  const inProgressCount = notes.filter((n) => n.in_progress && !n.completed).length;

  return (
    <div className="App relative min-h-screen w-full board-bg" data-testid="app-root">
      {showContracts && (
        <ContractsView
          onExit={() => {
            setShowContracts(false);
            setPendingContractId(null);
          }}
          onRenewed={() => fetchNotes()}
          initialContractId={pendingContractId}
        />
      )}
      {showContracts && <Toaster position="bottom-right" richColors />}
      {showBalances && (
        <BalancesView onExit={() => setShowBalances(false)} />
      )}
      {showBalances && <Toaster position="bottom-right" richColors />}
      {showCodes && <CodesView onExit={() => setShowCodes(false)} />}
      {showCodes && <Toaster position="bottom-right" richColors />}

      {/* PIN gate for ΚΩΔΙΚΟΙ */}
      <Dialog open={codesPinOpen} onOpenChange={setCodesPinOpen}>
        <DialogContent
          className="sm:max-w-[360px]"
          data-testid="codes-pin-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-black uppercase tracking-wider">
              <Key size={20} weight="bold" />
              ΚΩΔΙΚΟΣ ΠΡΟΣΒΑΣΗΣ
            </DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (codesPin === "2650") {
                setCodesPinOpen(false);
                setShowCodes(true);
                setCodesPin("");
                setCodesPinError("");
              } else {
                setCodesPinError("ΛΑΘΟΣ ΚΩΔΙΚΟΣ");
              }
            }}
            className="space-y-3"
          >
            <input
              type="password"
              inputMode="numeric"
              autoFocus
              value={codesPin}
              onChange={(e) => {
                setCodesPin(e.target.value);
                setCodesPinError("");
              }}
              data-testid="codes-pin-input"
              className="w-full px-4 py-3 rounded-xl border-2 border-ink/15 focus:border-ink text-2xl text-center tracking-widest font-bold"
              placeholder="••••"
            />
            {codesPinError && (
              <p
                className="text-xs font-bold text-red-700 text-center uppercase tracking-wider"
                data-testid="codes-pin-error"
              >
                {codesPinError}
              </p>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setCodesPinOpen(false)}
                className="px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider text-ink/70 hover:text-ink hover:bg-black/5"
              >
                ΑΚΥΡΟ
              </button>
              <button
                type="submit"
                data-testid="codes-pin-submit"
                className="px-5 py-2 rounded-full bg-ink text-white text-xs font-bold uppercase tracking-wider hover:bg-ink/85 active:scale-95"
              >
                ΕΙΣΟΔΟΣ
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <div style={showContracts || showBalances || showCodes ? { display: "none" } : undefined}>
      {/* Header */}
      <header className={`sticky top-0 z-30 board-bg/95 backdrop-blur-sm border-b border-black/5 ${isMobile ? "mobile-header" : ""}`}>
        <div className={`${isMobile ? "px-3 pt-3 pb-2 flex flex-col gap-2" : "px-6 pt-4 pb-2 flex items-center justify-between gap-4 flex-wrap"}`}>
          <div className="flex items-center justify-between gap-2">
            <div>
              <h1
                className={`font-caveat ${isMobile ? "text-3xl" : "text-4xl sm:text-5xl"} font-bold text-ink leading-none`}
                data-testid="brand-title"
              >
                {BRAND}
              </h1>
              <p className="text-[10px] uppercase tracking-[0.2em] text-ink-muted mt-1 font-bold">
                {activeCount} ΕΝΕΡΓΑ
                {inProgressCount > 0 ? ` · ${inProgressCount} ΣΕ ΕΞΕΛΙΞΗ` : ""}
              </p>
            </div>
            {isMobile && (
              <button
                onClick={() => setViewMode("desktop")}
                data-testid="mobile-to-desktop"
                title="ΕΝΑΛΛΑΓΗ ΣΕ PC LAYOUT"
                className="p-2 rounded-full border-2 border-ink/15 bg-white/60 text-ink active:scale-95"
              >
                <Desktop size={16} weight="bold" />
              </button>
            )}
          </div>

          {/* Centered date/time banner */}
          {!isMobile && <ClockBanner />}

          <div className={`flex items-center ${isMobile ? "gap-1.5 flex-wrap" : "gap-2"}`}>
            <button
              onClick={() => setScanInputOpen(true)}
              className={`${isMobile ? "px-3 py-2 text-[11px]" : "px-4 py-2.5 text-xs"} rounded-full flex items-center gap-1.5 transition-all font-bold uppercase tracking-wider border-2 bg-white/60 text-ink border-ink/15 hover:border-ink/40 active:scale-95`}
              data-testid="scan-btn"
              title="ΣΑΡΩΣΗ BARCODE"
            >
              <Barcode size={14} weight="bold" />
              ΣΑΡΩΣΗ
            </button>
            <button
              onClick={() => openCreate("task")}
              className={`bg-ink text-white ${isMobile ? "pl-3 pr-4 py-2 text-[11px]" : "pl-4 pr-5 py-2.5 text-sm"} rounded-full flex items-center gap-1.5 hover:bg-ink/85 active:scale-95 transition-all font-bold uppercase tracking-wider`}
              data-testid="create-note-btn"
            >
              <Plus size={16} weight="bold" />
              ΝΕΑ ΚΑΤΑΧΩΡΗΣΗ
            </button>

            <button
              onClick={() => setShowContracts(true)}
              data-testid="contracts-btn"
              className={`${isMobile ? "px-3 py-2 text-[11px]" : "px-4 py-2.5 text-xs"} rounded-full flex items-center gap-1.5 transition-all font-bold uppercase tracking-wider border-2 bg-purple-50 text-purple-900 border-purple-200 hover:border-purple-400 active:scale-95`}
              title="ΗΜΕΡΟΛΟΓΙΟ ΑΝΑΝΕΩΣΕΩΝ"
            >
              <CalendarIcon size={14} weight="bold" />
              ΑΝΑΝΕΩΣΕΙΣ
            </button>

            <button
              className={`${isMobile ? "px-2.5 py-2 text-[11px]" : "px-3 py-2.5 text-xs"} rounded-full flex items-center gap-1.5 transition-all font-bold uppercase tracking-wider border-2 bg-white/60 text-ink border-ink/15 hover:border-ink/40 active:scale-95`}
              data-testid="theme-toggle-btn"
              onClick={() => setTheme(isSerious ? "postit" : "serious")}
              title={isSerious ? "ΕΝΑΛΛΑΓΗ ΣΕ POSTIT" : "ΕΝΑΛΛΑΓΗ ΣΕ ΣΟΒΑΡΟ"}
            >
              {isSerious ? <Smiley size={16} weight="bold" /> : <Briefcase size={16} weight="bold" />}
              <span className={isMobile ? "" : "hidden md:inline"}>
                {isSerious ? "POSTIT" : "ΣΟΒΑΡΟ"}
              </span>
            </button>

            <button
              className={`${isMobile ? "px-2.5 py-2 text-[11px]" : "px-3 py-2.5 text-xs"} rounded-full flex items-center gap-1.5 transition-all font-bold uppercase tracking-wider border-2 bg-white/60 text-ink border-ink/15 hover:border-ink/40 active:scale-95`}
              data-testid="switch-user-btn"
              onClick={() => switchEmployee()}
              title="ΑΛΛΑΓΗ ΧΡΗΣΤΗ"
            >
              <UserSwitch size={16} weight="bold" />
              <span className={isMobile ? "" : "hidden sm:inline"}>
                {activeEmployee?.display_name
                  ? activeEmployee.display_name.slice(0, 12)
                  : "ΧΡΗΣΤΗΣ"}
              </span>
            </button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="px-3 py-2.5 rounded-full flex items-center gap-2 transition-all text-xs font-bold uppercase tracking-wider border-2 bg-white/60 text-ink border-ink/15 hover:border-ink/40 active:scale-95"
                  data-testid="menu-btn"
                  title="ΜΕΝΟΥ"
                >
                  <MenuIcon size={16} weight="bold" />
                  ΜΕΝΟΥ
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-64 rounded-xl shadow-xl"
              >
                <DropdownMenuLabel className="text-[9px] uppercase tracking-[0.18em] text-ink/55 font-bold">
                  {isSupervisor ? "SUPERVISOR" : "ΧΡΗΣΤΗΣ"} · {user?.display_name}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => setShowBalances(true)}
                  data-testid="menu-balances"
                  className="font-bold uppercase tracking-wider text-xs"
                >
                  <CurrencyEur size={14} className="mr-2" weight="bold" />
                  ΥΠΟΛΟΙΠΑ
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setShowContracts(true)}
                  data-testid="menu-contracts"
                  className="font-bold uppercase tracking-wider text-xs"
                >
                  <CalendarIcon size={14} className="mr-2" weight="bold" />
                  ΑΝΑΝΕΩΣΕΙΣ
                </DropdownMenuItem>
                {isSupervisor && (
                  <DropdownMenuItem
                    onClick={() => {
                      setCodesPin("");
                      setCodesPinError("");
                      setCodesPinOpen(true);
                    }}
                    data-testid="menu-codes"
                    className="font-bold uppercase tracking-wider text-xs"
                  >
                    <Key size={14} className="mr-2" weight="bold" />
                    ΚΩΔΙΚΟΙ
                  </DropdownMenuItem>
                )}
                {isSupervisor && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => setUsersOpen(true)}
                      data-testid="menu-new-user"
                      className="font-bold uppercase tracking-wider text-xs"
                    >
                      <UserPlus size={14} className="mr-2" weight="bold" />
                      ΝΕΟΣ USER
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setUsersOpen(true)}
                      data-testid="menu-manage-users"
                      className="font-bold uppercase tracking-wider text-xs"
                    >
                      <MenuIcon size={14} className="mr-2" weight="bold" />
                      ΔΙΑΧΕΙΡΙΣΗ ΧΡΗΣΤΩΝ
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => setDeletedOpen(true)}
                      data-testid="menu-deleted"
                      className="font-bold uppercase tracking-wider text-xs"
                    >
                      <TrashSimple size={14} className="mr-2" weight="bold" />
                      ΔΙΕΓΡΑΜΕΝΑ
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setLogsOpen(true)}
                      data-testid="menu-logs"
                      className="font-bold uppercase tracking-wider text-xs"
                    >
                      <Article size={14} className="mr-2" weight="bold" />
                      LOGS
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setDbSizeOpen(true)}
                      data-testid="menu-dbsize"
                      className="font-bold uppercase tracking-wider text-xs"
                    >
                      <Database size={14} className="mr-2" weight="bold" />
                      ΜΕΓΕΘΟΣ ΒΑΣΗΣ
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setBackupOpen(true)}
                      data-testid="menu-backup"
                      className="font-bold uppercase tracking-wider text-xs"
                    >
                      <FloppyDisk size={14} className="mr-2" weight="bold" />
                      BACKUP
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => logout()}
                  data-testid="menu-logout"
                  className="font-bold uppercase tracking-wider text-xs text-red-600 focus:text-red-700"
                >
                  <SignOut size={14} className="mr-2" weight="bold" />
                  ΑΠΟΣΥΝΔΕΣΗ
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {false && !isSupervisor && (
              <button
                onClick={() => logout()}
                data-testid="employee-logout-btn"
                className="px-3 py-2.5 rounded-full flex items-center gap-1.5 transition-all text-xs font-bold uppercase tracking-wider border-2 bg-white/60 text-ink/70 border-ink/15 hover:border-red-300 hover:text-red-700 active:scale-95"
                title="ΑΠΟΣΥΝΔΕΣΗ"
              >
                <SignOut size={14} weight="bold" />
              </button>
            )}
          </div>
        </div>
        <div className="flex items-end justify-between gap-2 pr-6">
          <Tabs activeTabs={activeTabs} onChange={setActiveTabs} notes={notes} />
          {!activeTabs.has("completed") && !isMobile && (
            <button
              onClick={() =>
                setActiveView((v) => (v === "board" ? "table" : "board"))
              }
              data-testid="view-mode-toggle"
              title={activeView === "board" ? "ΠΡΟΒΟΛΗ ΠΙΝΑΚΑ" : "ΠΡΟΒΟΛΗ ΚΑΡΤΩΝ"}
              className="px-3 py-2 rounded-full flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider border-2 bg-white/70 text-ink border-ink/15 hover:border-ink/40 active:scale-95 shrink-0 mb-2"
            >
              {activeView === "board" ? (
                <>
                  <Table size={13} weight="bold" />
                  ΠΙΝΑΚΑΣ
                </>
              ) : (
                <>
                  <SquaresFour size={13} weight="bold" />
                  ΚΑΡΤΕΣ
                </>
              )}
            </button>
          )}
        </div>
      </header>

      {/* The board — sticky-note grid OR Excel-like table for ΕΚΤΕΛΕΣΜΕΝΑ */}
      {activeTabs.has("completed") ? (
        <div className="px-6 py-5">
          <NotesTable
            notes={filteredNotes}
            variant="completed"
            actions={(n) => (
              <>
                <button
                  onClick={() => handleUncomplete(n)}
                  data-testid={`table-restore-${n.id}`}
                  className="p-1.5 rounded-md text-green-700 hover:bg-green-50 transition-colors"
                  title="ΕΠΑΝΑΦΟΡΑ ΣΕ ΕΝΕΡΓΟ"
                >
                  <ArrowsClockwise size={14} weight="bold" />
                </button>
                <button
                  onClick={() => handlePrint(n)}
                  data-testid={`table-print-${n.id}`}
                  className="p-1.5 rounded-md text-ink/70 hover:bg-black/5 transition-colors"
                  title="ΕΚΤΥΠΩΣΗ"
                >
                  <Printer size={14} weight="bold" />
                </button>
                <button
                  onClick={() => handleDelete(n)}
                  data-testid={`table-delete-${n.id}`}
                  className="p-1.5 rounded-md text-red-600/70 hover:bg-red-50 hover:text-red-700 transition-colors"
                  title="ΔΙΑΓΡΑΦΗ"
                >
                  <Trash size={14} weight="bold" />
                </button>
              </>
            )}
          />
        </div>
      ) : activeView === "table" ? (
        <div className="px-6 py-5">
          <NotesTable
            notes={filteredNotes.filter((n) => !n.minimized)}
            variant="active"
            actions={(n) => (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid={`table-actions-${n.id}`}
                    title="ΜΕΝΟΥ ΕΡΓΑΣΙΩΝ"
                    className="p-1.5 rounded-md text-ink/70 hover:bg-black/5 transition-colors"
                  >
                    <DotsThreeVertical size={16} weight="bold" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuItem
                    onClick={() => openEdit(n)}
                    data-testid={`table-open-${n.id}`}
                    className="text-xs font-bold uppercase tracking-wider"
                  >
                    <Pencil size={14} className="mr-2" weight="bold" />
                    ΑΝΟΙΓΜΑ
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => handlePrint(n)}
                    className="text-xs font-bold uppercase tracking-wider"
                  >
                    <Printer size={14} className="mr-2" weight="bold" />
                    ΕΚΤΥΠΩΣΗ
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => handleToggleRead(n)}
                    data-testid={`table-anamoni-${n.id}`}
                    className={`text-xs font-bold uppercase tracking-wider ${
                      n.read ? "text-orange-700 focus:text-orange-800" : ""
                    }`}
                  >
                    <Briefcase size={14} className="mr-2" weight="bold" />
                    {n.read ? "ΑΚΥΡΩΣΗ ΑΝΑΜΟΝΗΣ" : "ΑΝΑΜΟΝΗ"}
                  </DropdownMenuItem>
                  {n.in_progress ? (
                    <DropdownMenuItem
                      onClick={() => handleSetInProgress(n, null)}
                      data-testid={`table-unassign-${n.id}`}
                      className="text-xs font-bold uppercase tracking-wider text-red-600 focus:text-red-700"
                    >
                      <UserSwitch size={14} className="mr-2" weight="bold" />
                      ΑΚΥΡΩΣΗ ΑΝΑΘΕΣΗΣ ({n.in_progress_by || "—"})
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger
                        data-testid={`table-assign-${n.id}`}
                        className="text-xs font-bold uppercase tracking-wider"
                      >
                        <UserSwitch size={14} className="mr-2" weight="bold" />
                        ΑΝΑΘΕΣΗ ΣΕ...
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent>
                        {PEOPLE.map((p) => (
                          <DropdownMenuItem
                            key={p}
                            onClick={() => handleSetInProgress(n, p)}
                            data-testid={`table-assign-${n.id}-${p}`}
                            className="text-xs font-bold uppercase tracking-wider"
                          >
                            {p}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => handleComplete(n)}
                    className="text-xs font-bold uppercase tracking-wider text-emerald-700 focus:text-emerald-800"
                  >
                    <CheckCircle size={14} className="mr-2" weight="bold" />
                    ΟΛΟΚΛΗΡΩΣΗ
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => handleCharged(n)}
                    className="text-xs font-bold uppercase tracking-wider"
                  >
                    <CurrencyEur size={14} className="mr-2" weight="bold" />
                    ΠΡΟΣ ΧΡΕΩΣΗ
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => handleMinimize(n)}
                    className="text-xs font-bold uppercase tracking-wider"
                  >
                    <Briefcase size={14} className="mr-2" weight="bold" />
                    {n.minimized ? "ΕΠΑΝΑΦΟΡΑ" : "ΕΛΑΧΙΣΤΟΠΟΙΗΣΗ"}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => handleDelete(n)}
                    className="text-xs font-bold uppercase tracking-wider text-red-600 focus:text-red-700"
                  >
                    <Trash size={14} className="mr-2" weight="bold" />
                    ΔΙΑΓΡΑΦΗ
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          />
        </div>
      ) : (
        <PinBoard
          columns={visibleColumns}
          loading={loading}
          onReorder={handleReorder}
          onEdit={openEdit}
          onDelete={handleDelete}
          onComplete={handleComplete}
          onUncomplete={handleUncomplete}
          onCharged={handleCharged}
          onChangeCategory={handleChangeCategory}
          onToggleRead={handleToggleRead}
          onSetInProgress={handleSetInProgress}
          onMinimize={handleMinimize}
          onPrint={handlePrint}
          onPrintLabel={handlePrintLabel}
          onUpdateField={(id, patch) => handleUpdate(id, patch)}
          onPartialExecute={openPartial}
          onOpenContract={openContractById}
          isMobile={isMobile}
          types={NOTE_TYPES}
          channels={CHANNELS}
          people={PEOPLE}
          minimizedNotes={minimizedNotes}
          onToggleMinimizeAll={handleToggleMinimizeAll}
          isAllMinimized={isAllMinimized}
        />
      )}

      {/* Custom edge scrollbars removed per user request */}

      {/* Create/Edit dialog */}
      <NoteDialog
        open={dialogOpen}
        onOpenChange={(o) => {
          setDialogOpen(o);
          if (!o) setEditingNote(null);
        }}
        mode={dialogMode}
        initial={editingNote}
        defaultType={defaultType}
        types={NOTE_TYPES}
        channels={CHANNELS}
        people={PEOPLE}
        onSubmit={dialogMode === "create" ? handleCreate : handleSaveEdit}
      />

      <Toaster position="bottom-right" richColors />

      {/* Scan workflow dialog */}
      <ScanDialog
        open={scanOpen}
        onOpenChange={setScanOpen}
        note={scanNote}
        people={PEOPLE}
        mode={scanMode}
        onAssign={handleScanAssign}
        onComplete={handleScanComplete}
        onCorrect={handleScanCorrect}
      />

      {/* Partial execution dialog (ΠΑΡΑΓΓΕΛΙΑ items toggling) */}
      <PartialDialog
        open={partialOpen}
        onOpenChange={(o) => {
          setPartialOpen(o);
          if (!o) setPartialNote(null);
        }}
        note={partialNote}
        onSave={handlePartialSave}
      />

      {/* Users management dialog (supervisor only) */}
      <UsersDialog open={usersOpen} onOpenChange={setUsersOpen} />

      {/* Admin dialogs (supervisor only) */}
      <DeletedNotesDialog
        open={deletedOpen}
        onOpenChange={setDeletedOpen}
        onRestored={fetchNotes}
      />
      <LogsDialog open={logsOpen} onOpenChange={setLogsOpen} />
      <DbSizeDialog open={dbSizeOpen} onOpenChange={setDbSizeOpen} />
      <BackupDialog open={backupOpen} onOpenChange={setBackupOpen} />

      {/* Scan ID input dialog (for handheld scanner / manual paste / camera) */}
      <ScanInputDialog
        open={scanInputOpen}
        onOpenChange={(o) => {
          setScanInputOpen(o);
          if (!o) setCameraScanOpen(false);
        }}
      >
        <ScanInputContent className="sm:max-w-[420px]" data-testid="scan-input-dialog">
          <ScanInputHeader>
            <ScanInputTitle className="font-caveat text-3xl">ΣΑΡΩΣΗ BARCODE</ScanInputTitle>
          </ScanInputHeader>
          <div className="space-y-3">
            <p className="text-xs uppercase tracking-wider font-bold text-ink/60">
              ΣΑΡΩΣΕ BARCODE ή ΠΛΗΚΤΡΟΛΟΓΗΣΕ ΑΡΙΘΜΟ POST
            </p>
            <ScanTextInput
              autoFocus={!cameraScanOpen}
              value={scanInputValue}
              onChange={(e) => setScanInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  submitScanInput();
                }
              }}
              placeholder="π.χ. 000123"
              className="font-arial text-sm"
              data-testid="scan-input-text"
            />
            {!cameraScanOpen && (
              <button
                type="button"
                onClick={() => setCameraScanOpen(true)}
                data-testid="open-camera-scan"
                className="w-full px-3 py-2.5 rounded-xl bg-emerald-600 text-white font-bold text-xs uppercase tracking-wider flex items-center justify-center gap-2 hover:bg-emerald-700 active:scale-[0.98]"
              >
                <Camera size={14} weight="bold" />
                ΣΑΡΩΣΗ ΜΕ ΚΑΜΕΡΑ
              </button>
            )}
            {cameraScanOpen && (
              <CameraScan
                onClose={() => setCameraScanOpen(false)}
                onDetected={(text) => {
                  setCameraScanOpen(false);
                  setScanInputValue(text);
                  setScanInputOpen(false);
                  setScanInputValue("");
                  openScanById(text);
                }}
              />
            )}
            <div className="flex justify-end gap-2 pt-1">
              <button
                onClick={() => {
                  setScanInputOpen(false);
                  setScanInputValue("");
                  setCameraScanOpen(false);
                }}
                className="px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider text-ink/70 hover:text-ink hover:bg-black/5"
                data-testid="scan-input-cancel"
              >
                ΑΚΥΡΟ
              </button>
              <button
                onClick={submitScanInput}
                className="px-5 py-2 rounded-full text-xs font-bold uppercase tracking-wider bg-ink text-white hover:bg-ink/85 active:scale-95"
                data-testid="scan-input-submit"
              >
                ΑΝΑΖΗΤΗΣΗ
              </button>
            </div>
          </div>
        </ScanInputContent>
      </ScanInputDialog>
      </div>
    </div>
  );
}

export default App;

// Greek day names (uppercase, without accents)
const GR_DAYS = [
  "ΚΥΡΙΑΚΗ",
  "ΔΕΥΤΕΡΑ",
  "ΤΡΙΤΗ",
  "ΤΕΤΑΡΤΗ",
  "ΠΕΜΠΤΗ",
  "ΠΑΡΑΣΚΕΥΗ",
  "ΣΑΒΒΑΤΟ",
];

function formatClock(d) {
  const day = GR_DAYS[d.getDay()];
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return { day, date: `${dd}/${mm}/${yy}`, time: `${hh}:${mi}` };
}

function ClockBanner() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    // Tick every 15s — minute precision is enough; align to next minute boundary
    const tick = () => setNow(new Date());
    const interval = setInterval(tick, 15_000);
    return () => clearInterval(interval);
  }, []);
  const { day, date, time } = formatClock(now);
  return (
    <div
      className="flex items-center gap-3 px-5 py-2 rounded-2xl bg-ink text-white shadow-md border border-ink/20"
      data-testid="clock-banner"
      title="ΗΜΕΡΟΜΗΝΙΑ / ΩΡΑ"
    >
      <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-amber-300/90">
        {day}
      </div>
      <div className="w-px h-5 bg-white/25" />
      <div className="font-arial font-bold text-sm tracking-[0.1em]">
        {date}
      </div>
      <div className="w-px h-5 bg-white/25" />
      <div className="font-arial font-black text-base tracking-[0.12em] text-amber-300">
        {time}
      </div>
    </div>
  );
}
